import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../App'
import { api, Conversation, Group } from '../services/api'
import { useSharedWebSocket } from '../contexts/WebSocketContext'

type TabType = 'chats' | 'groups' | 'profile'

export default function ChatListPage() {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [groups, setGroups] = useState<Group[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<TabType>('chats')
  const [showNewChatModal, setShowNewChatModal] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [searchQuery] = useState('')
  const [newChatUsername, setNewChatUsername] = useState('')

  const navigate = useNavigate()
  const { logout } = useAuth()
  const { isConnected, lastMessage } = useSharedWebSocket()

  // Load conversations and groups
  useEffect(() => {
    loadConversations()
    loadGroups()
  }, [])

  const loadGroups = async () => {
    try {
      const data = await api.getGroups()
      setGroups(data)
    } catch (error) {
      console.error('Failed to load groups:', error)
    }
  }

  // Handle incoming messages
  useEffect(() => {
    if (lastMessage?.type === 'message') {
      loadConversations() // Refresh list on new message
    }
  }, [lastMessage])

  // Polling fallback when WebSocket is not connected
  useEffect(() => {
    if (isConnected) return // Don't poll if WebSocket is connected

    console.log('[ChatList] WebSocket not connected, starting polling...')
    const pollInterval = setInterval(() => {
      loadConversations()
    }, 5000) // Poll every 5 seconds

    return () => clearInterval(pollInterval)
  }, [isConnected])

  const loadConversations = async () => {
    try {
      setIsLoading(true)
      const data = await api.getConversations()
      setConversations(data)
    } catch (error) {
      console.error('Failed to load conversations:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now.getTime() - date.getTime()

    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor(diff / (1000 * 60))

    if (days > 7) {
      // Use locale-aware date format
      return date.toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' })
    } else if (days > 0) {
      return `${days}天前`
    } else if (hours > 0) {
      return `${hours}小時前`
    } else if (minutes > 0) {
      return `${minutes}分鐘前`
    } else {
      return '剛剛'
    }
  }

  const filteredConversations = conversations.filter(conv =>
    (conv.recipientName || conv.recipientId)
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  )

  const handleStartNewChat = async () => {
    if (!newChatUsername.trim()) return

    try {
      // First, try to find user by username to get their UUID
      const users = await api.searchUsers(newChatUsername.trim())
      const exactMatch = users.find(u =>
        u.username.toLowerCase() === newChatUsername.trim().toLowerCase()
      )

      if (exactMatch) {
        // Use UUID for navigation (critical for E2EE key derivation)
        navigate(`/chat/${exactMatch.id}`)
      } else {
        // Fallback: use username directly (E2EE may not work correctly)
        console.warn('[Chat] User not found, using username as ID')
        navigate(`/chat/${newChatUsername.trim()}`)
      }

      setShowNewChatModal(false)
      setNewChatUsername('')
    } catch (error) {
      console.error('Failed to find user:', error)
      // Fallback: use username directly
      navigate(`/chat/${newChatUsername.trim()}`)
      setShowNewChatModal(false)
      setNewChatUsername('')
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* App Bar */}
      <header className="app-bar">
        <h1 className="text-xl font-semibold flex-1">LYNKSTAR</h1>

        {/* Connection Status */}
        <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-secure-green' : 'bg-gray-400'}`} />

        {/* Search Button */}
        <button
          onClick={() => navigate('/search')}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>

        {/* Menu Button */}
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {showMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-surface rounded-xl shadow-lg z-50 overflow-hidden animate-fade-in">
              <button
                onClick={() => {
                  setShowMenu(false)
                  navigate('/group/create')
                }}
                className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                New Group
              </button>
              <button
                onClick={() => {
                  setShowMenu(false)
                  navigate('/settings')
                }}
                className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </button>
              <button
                onClick={handleLogout}
                className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner border-primary border-t-primary-light" />
          </div>
        ) : activeTab === 'chats' ? (
          /* Chats Tab */
          filteredConversations.length === 0 ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center h-64 text-center px-8">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 text-gray-300 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <h2 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">
                No conversations yet
              </h2>
              <p className="text-gray-400 dark:text-gray-500">
                Start a new chat to begin messaging
              </p>
            </div>
          ) : (
            /* Conversation List */
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {filteredConversations.map((conversation) => (
                <button
                  key={conversation.recipientId}
                  onClick={() => navigate(`/chat/${conversation.recipientId}`)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-dark-surface transition-colors"
                >
                  {/* Avatar */}
                  <div className="relative">
                    <div className="avatar">
                      {conversation.recipientAvatar ? (
                        <img
                          src={conversation.recipientAvatar}
                          alt={conversation.recipientName || conversation.recipientId}
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        (conversation.recipientName || conversation.recipientId)
                          .charAt(0)
                          .toUpperCase()
                      )}
                    </div>
                    {conversation.isOnline && (
                      <div className="absolute bottom-0 right-0 online-indicator" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {conversation.recipientName || conversation.recipientId}
                      </h3>
                      {conversation.lastMessage && (
                        <span className={`text-xs ${conversation.unreadCount > 0 ? 'text-primary font-medium' : 'text-gray-400'}`}>
                          {formatTime(conversation.lastMessage.timestamp)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {/* Encryption indicator */}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-secure-green flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate flex-1">
                        {conversation.lastMessage?.text || 'Start a conversation'}
                      </p>
                      {conversation.unreadCount > 0 && (
                        <span className="unread-badge">
                          {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )
        ) : (
          /* Groups Tab */
          groups.length === 0 ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center h-64 text-center px-8">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 text-gray-300 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h2 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">
                No groups yet
              </h2>
              <p className="text-gray-400 dark:text-gray-500 mb-4">
                Create a group to start chatting
              </p>
              <button
                onClick={() => navigate('/group/create')}
                className="btn-primary"
              >
                Create Group
              </button>
            </div>
          ) : (
            /* Groups List */
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {groups.map((group) => (
                <button
                  key={group.id}
                  onClick={() => navigate(`/group/${group.id}`)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-dark-surface transition-colors"
                >
                  {/* Avatar */}
                  <div className="avatar bg-primary">
                    {group.avatar_url ? (
                      <img
                        src={group.avatar_url}
                        alt={group.name}
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {group.name}
                      </h3>
                      <span className="text-xs text-gray-400">
                        {group.member_count} members
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      {/* Role badge */}
                      {group.my_role === 'owner' && (
                        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                          Owner
                        </span>
                      )}
                      {group.my_role === 'admin' && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                          Admin
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )
        )}
      </main>

      {/* Floating Action Button */}
      <button
        onClick={() => activeTab === 'chats' ? setShowNewChatModal(true) : navigate('/group/create')}
        className="fab fixed right-6 bottom-24"
      >
        {activeTab === 'chats' ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        )}
      </button>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <button
          onClick={() => setActiveTab('chats')}
          className={`bottom-nav-item ${activeTab === 'chats' ? 'active' : ''}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${activeTab === 'chats' ? 'fill-current' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={activeTab === 'chats' ? 2.5 : 2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <span className="text-xs">Chats</span>
        </button>
        <button
          onClick={() => setActiveTab('groups')}
          className={`bottom-nav-item ${activeTab === 'groups' ? 'active' : ''}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${activeTab === 'groups' ? 'fill-current' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={activeTab === 'groups' ? 2.5 : 2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span className="text-xs">Groups</span>
        </button>
        <button
          onClick={() => navigate('/profile')}
          className="bottom-nav-item"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="text-xs">Profile</span>
        </button>
      </nav>

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50" onClick={() => setShowNewChatModal(false)}>
          <div
            className="bg-white dark:bg-dark-surface w-full max-w-lg rounded-t-2xl p-6 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Start New Chat
            </h2>

            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                value={newChatUsername}
                onChange={(e) => setNewChatUsername(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleStartNewChat()}
                placeholder="Enter username"
                className="input-field pl-12"
                autoFocus
              />
            </div>

            <button
              onClick={handleStartNewChat}
              disabled={!newChatUsername.trim()}
              className="btn-primary w-full mb-4"
            >
              Start Chat
            </button>

            <button className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
              </div>
              <div>
                <p className="font-medium">Scan QR Code</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Verify identity and start chat</p>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Click outside to close menu */}
      {showMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
      )}
    </div>
  )
}
